import { defineStore } from 'pinia'
import router, { constantRoutes, asyncRoutes, scopeList } from '@/router/index.js'
import { useUserStore } from '@/stores/modules/user'
import { useMerchantStore } from '@/stores/modules/merchant'
import { hasPermission as _hasPermission } from '@/utils/permission'

export const usePermissionStore = defineStore('permission', {
  state: () => {
    return {
      // 当前选中的域名作用域（页面顶部菜单）
      routerScope: 'spm-platform',
      // 当前用户角色可访问的域名作用域（页面顶部菜单列表）
      scopeList: [],
      // 当前用户可访问的路由集合
      routes: []
    }
  },

  actions: {
    setRoutes(routes) {
      this.routes = constantRoutes.concat(routes)
    },

    /**
     * 点击顶部菜单栏，切换路由作用域
     * @param scope {String}
     * @param pushFirstRouter {Boolean} 是否跳转到第一个路由
     */
    changeRouterScope({ scope, pushFirstRouter }) {
      const userStore = useUserStore()
      const accessedRoutes = filterScopeAsyncRoutes(asyncRoutes, scope, userStore.rolePermission)
      // console.log('点击顶部菜单栏，切换路由作用域', accessedRoutes, scope)

      this.setRoutes(accessedRoutes)
      this.routerScope = scope

      // 默认选中第一个动态路由元素
      if (pushFirstRouter && accessedRoutes.length > 0) {
        if (accessedRoutes[0].children && accessedRoutes[0].children.length > 0) {
          router.push(accessedRoutes[0].children[0].formatted_full_path)
        } else {
          router.push(accessedRoutes[0].path)
        }
      }

      return accessedRoutes
    },

    /**
     * 生成角色可访问的所有可访问路由
     * @param rolePermission
     * @param roles
     */
    generateRoutes({ rolePermission }) {
      const accessedRoutes = filterRoleAsyncRoutes(asyncRoutes, rolePermission)
      const accessedScopeList = filterRoleScopeList(scopeList, rolePermission, accessedRoutes)

      this.setRoutes(accessedRoutes)
      this.scopeList = accessedScopeList
      return accessedRoutes
    }
  }
})

/**
 * 过滤角色以及路由作用域对应可访问的路由
 * @param routes
 * @param scope
 * @param rolePermission
 * @returns {Array}
 */
function filterScopeAsyncRoutes(routes, scope, rolePermission) {
  const res = []

  routes.forEach((route) => {
    const tmp = { ...route }
    // 404 页
    if (tmp.name === 'not-found') {
      res.push(tmp)
      return
    }

    // 1. (如果有指定角色) 作用域通过之后再判断是否有角色权限。
    if (rolePermission && !hasPermission(rolePermission, tmp)) {
      return
    }

    // 2. （如果有指定作用域 scope）优先过滤作用域的路由。
    if (tmp.scope !== scope) {
      return
    }

    if (tmp.children) {
      tmp.children = filterScopeAsyncRoutes(tmp.children, scope, rolePermission)
    }

    // 不显示没有子菜单的侧边栏
    if (tmp.children && tmp.children.length === 0) {
      return
    }

    // 不显示子菜单全部隐藏的侧边栏
    if (tmp.children && tmp.children.every((child) => child.hidden)) {
      return
    }

    res.push(tmp)
  })

  return res
}

/**
 * 过滤左侧菜单显示的动态路由
 * 判断是否有角色权限。
 *
 * @param routes asyncRoutes // 动态路由
 * @param rolePermission
 */
export function filterRoleAsyncRoutes(routes, rolePermission) {
  const res = []

  routes.forEach((route) => {
    const tmp = { ...route }

    // 404 页
    if (tmp.name === 'not-found') {
      res.push(tmp)
      return
    }

    // 判断是否有角色权限。
    if (rolePermission && !hasPermission(rolePermission, tmp)) {
      return
    }

    if (tmp.children) {
      tmp.children = filterRoleAsyncRoutes(tmp.children, rolePermission)
    }

    // 没有有权限的页面，不显示父路由
    // 页面才有virtual_scope_name字段
    if (
      !Object.hasOwnProperty.call(tmp, 'virtual_scope_name') &&
      (!tmp.children || !tmp.children.length)
    ) {
      return
    }

    res.push(tmp)
  })

  return res
}

function hasPermission(rolePermission, route) {
  if (route.meta) {
    if (isHiddenInRoute(route.meta)) return false
  }

  if (rolePermission.has_all_permissions) {
    return true
  }

  if (route?.meta?.vPermission) {
    return _hasPermission(route?.meta?.vPermission, rolePermission)
  }
  return true
}

/**
 * 根据meta决定是否隐藏该路由
 */
function isHiddenInRoute(data) {
  if (!data) return false
  if (typeof data.isHiddenRoute === 'function') {
    return data.isHiddenRoute()
  }
  return data.isHiddenRoute
}

/**
 * 根据当前的商户类型判断(路由作用域、一级路由、二级路由)是否有权限
 * @param meta 路由元信息
 * @return {boolean}
 */
function hasMerchantTypePermission(meta) {
  if (!meta || !meta.merchant_types || !meta.merchant_types.length) {
    return true
  }
  const merchantStore = useMerchantStore()
  if (!merchantStore.merchantInfo || !merchantStore.merchantInfo.type) {
    return false
  }
  // TODO: 发现加了打印之后，可以正常出来管理中心，待排查问题
  console.log('merchantInfo', merchantStore.merchantInfo)
  return meta.merchant_types.includes(merchantStore.merchantInfo.type)
}

/**
 * 过滤角色可访问的路由作用域
 * @param scopeList
 * @param rolePermission
 * @param accessedRoutes {Array} 全部有权限的路由
 * @returns {Array}
 */
function filterRoleScopeList(scopeList, rolePermission, accessedRoutes) {
  console.log('过滤角色可访问的路由作用域', scopeList, accessedRoutes)
  const res = []

  scopeList.forEach((scope) => {
    const tmp = { ...scope }

    if (tmp.children) {
      tmp.children = filterRoleScopeList(tmp.children, rolePermission, accessedRoutes)
      // 当子元素只有 1 个的时候，显示直接点击的菜单项
      if (tmp.children && tmp.children.length === 1) {
        res.push(tmp.children[0])
      } else if (tmp.children && tmp.children.length > 1) {
        // 子元素大于 1 个的时候，显示下拉列表
        res.push(tmp)
      }
      return
    } else {
      const hasRoutes = accessedRoutes && accessedRoutes.some((route) => route.scope === scope.name) // 路由作用域是否包含路由
      //  过滤掉没有路由的路由作用域
      if (!hasRoutes) {
        return
      }
    }

    // 判断是否有角色权限
    if (rolePermission && !hasPermission(rolePermission, tmp)) {
      return
    }

    res.push(tmp)
  })

  return res
}
